import { useEffect, useState, useRef } from "react";

const HIDE_AFTER_MS = 8000;

interface UsePlayerAutoHideProps {
  isActuallyPlaying: boolean;
  isExpanded: boolean;
}

// Slides the collapsed mini-player off-screen after a stretch of being
// paused and untouched, so a track left paused doesn't permanently eat
// screen space. It reappears instantly the moment playback resumes from
// anywhere in the app (the track page it points to always has its own
// play button, so pausing never strands playback — only this bar's
// visibility).
export function usePlayerAutoHide({
  isActuallyPlaying,
  isExpanded,
}: UsePlayerAutoHideProps) {
  const [isHidden, setIsHidden] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Always visible while expanded or actively playing.
    if (isExpanded || isActuallyPlaying) {
      setIsHidden(false);
      return;
    }

    timeoutRef.current = setTimeout(() => setIsHidden(true), HIDE_AFTER_MS);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isActuallyPlaying, isExpanded]);

  // Tapping the hidden sliver reveals the bar again without acting on
  // whatever is underneath the tap.
  const reveal = () => setIsHidden(false);

  return { isHidden, reveal };
}
